import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

let _lastUpgradeToast = 0;

// Region-block (451) → block screen. Pro-gate (402) → global upgrade prompt.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const d = err?.response?.data;
    const status = err?.response?.status;
    if (status === 451 || d?.blocked) {
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/region-blocked")) {
        window.location.replace("/region-blocked");
      }
    }
    if (status === 402 && typeof window !== "undefined") {
      const now = Date.now();
      if (now - _lastUpgradeToast > 1500) {
        _lastUpgradeToast = now;
        toast.error(d?.detail || "This is a Pro feature — upgrade to UC Engineer Pro to unlock it.", {
          action: {
            label: "Upgrade",
            onClick: () => window.location.assign("/billing"),
          },
          duration: 6000,
        });
      }
    }
    return Promise.reject(err);
  }
);

// ------------------------- Analytics -------------------------
export const trackVisit = (payload) => api.post("/analytics/track", payload).then((r) => r.data).catch(() => {});
export const getAnalyticsSummary = (scope = "mine", days = 30) => api.get("/analytics/summary", { params: { scope, days } }).then((r) => r.data);
export const getAdminUsers = () => api.get("/admin/users").then((r) => r.data);

// ------------------------- Support tickets -------------------------
export const createSupportTicket = (payload) => api.post("/support/tickets", payload).then((r) => r.data);
export const getMySupportTickets = () => api.get("/support/tickets").then((r) => r.data);
export const getAdminSupportTickets = () => api.get("/admin/support/tickets").then((r) => r.data);
export const updateSupportTicket = (id, status) => api.patch(`/admin/support/tickets/${id}`, { status }).then((r) => r.data);
export const replySupportTicket = (id, reply) => api.post(`/admin/support/tickets/${id}/reply`, { reply }).then((r) => r.data);

// ------------------------- Auth -------------------------
export const authRegister = (payload) => api.post("/auth/register", payload).then((r) => r.data);
export const authLogin = (payload) => api.post("/auth/login", payload).then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authGoogleSession = (session_id) => api.post("/auth/session", { session_id }).then((r) => r.data);
export const authForgotPassword = (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const authResetPassword = (token, password) => api.post("/auth/reset-password", { token, password }).then((r) => r.data);

// ------------------------- Team seats / invites -------------------------
export const getTeam = () => api.get("/team").then((r) => r.data);
export const inviteMember = (email, role = "member") => api.post("/team/invite", { email, role }).then((r) => r.data);
export const revokeInvite = (token) => api.delete(`/team/invite/${token}`).then((r) => r.data);
export const removeMember = (userId) => api.delete(`/team/member/${userId}`).then((r) => r.data);
export const getInviteInfo = (token) => api.get(`/team/invite/${token}`).then((r) => r.data);

// ------------------------- Billing / subscriptions -------------------------
export const getPaymentsConfig = () => api.get("/payments/config").then((r) => r.data);
export const migrateSubscribers = () => api.post("/payments/admin/migrate-subscribers").then((r) => r.data);
export const createCheckout = (origin_url, plan = "monthly") => api.post("/payments/checkout", { origin_url, plan }).then((r) => r.data);
export const getPaymentStatus = (sid) => api.get(`/payments/status/${sid}`).then((r) => r.data);
export const getWorkspaceLimits = () => api.get("/workspace/limits").then((r) => r.data);
export const getEntitlements = () => api.get("/workspace/entitlements").then((r) => r.data);
export const getOverviewStats = () => api.get("/overview/stats").then((r) => r.data);

// ------------------------- Device inventory & compatibility -------------------------
export const getDeviceCatalog = () => api.get("/devices/catalog").then((r) => r.data);
export const getDevices = (siteId, track = "webex") => api.get(`/devices/${siteId}`, { params: { track } }).then((r) => r.data);
export const addDevice = (siteId, body, track = "webex") => api.post(`/devices/${siteId}`, body, { params: { track } }).then((r) => r.data);
export const updateDevice = (siteId, id, body, track = "webex") => api.put(`/devices/${siteId}/${id}`, body, { params: { track } }).then((r) => r.data);
export const deleteDevice = (siteId, id) => api.delete(`/devices/${siteId}/${id}`).then((r) => r.data);
export const importDevices = (siteId, rows, track = "webex", replace = false) =>
  api.post(`/devices/${siteId}/import`, { rows, replace }, { params: { track } }).then((r) => r.data);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const getSymptoms = () => api.get("/symptoms").then((r) => r.data);
export const getSymptom = (id) => api.get(`/symptoms/${id}`).then((r) => r.data);
export const getSymptomsFull = () => api.get("/symptoms/full").then((r) => r.data);
export const matchSymptom = (description) => api.post("/symptoms/match", { description }).then((r) => r.data);
export const getHealthChecks = () => api.get("/health-checks").then((r) => r.data);
export const getHealthCheck = (id) => api.get(`/health-checks/${id}`).then((r) => r.data);
export const getMigration = () => api.get("/migration").then((r) => r.data);
export const getMigrationSites = () => api.get("/migration/sites").then((r) => r.data);
export const createMigrationSite = (payload) => api.post("/migration/sites", payload).then((r) => r.data);
export const deleteMigrationSite = (id) => api.delete(`/migration/sites/${id}`).then((r) => r.data);
export const getNotifications = () => api.get("/notifications").then((r) => r.data);
export const getSettings = () => api.get("/settings").then((r) => r.data);
export const updateSettings = (payload) => api.put("/settings", payload).then((r) => r.data);
export const bulkCreateSites = (rows) => api.post("/migration/sites/bulk", { rows }).then((r) => r.data);
export const getVapidKey = () => api.get("/push/vapid-public-key").then((r) => r.data.key);
export const pushSubscribe = (subscription) => api.post("/push/subscribe", { subscription }).then((r) => r.data);
export const pushUnsubscribe = (subscription) => api.post("/push/unsubscribe", { subscription }).then((r) => r.data);
export const pushTest = () => api.post("/push/test").then((r) => r.data);
export const updateMigrationSite = (id, payload) =>
  api.put(`/migration/sites/${id}`, payload).then((r) => r.data);

export const getLicenses = () => api.get("/licenses").then((r) => r.data);
export const createLicense = (payload) => api.post("/licenses", payload).then((r) => r.data);
export const updateLicense = (id, payload) => api.put(`/licenses/${id}`, payload).then((r) => r.data);
export const deleteLicense = (id) => api.delete(`/licenses/${id}`).then((r) => r.data);
export const bulkCreateLicenses = (rows) => api.post("/licenses/bulk", { rows }).then((r) => r.data);

export const getCertificates = () => api.get("/certificates").then((r) => r.data);
export const createCertificate = (payload) => api.post("/certificates", payload).then((r) => r.data);
export const updateCertificate = (id, payload) => api.put(`/certificates/${id}`, payload).then((r) => r.data);
export const deleteCertificate = (id) => api.delete(`/certificates/${id}`).then((r) => r.data);
export const bulkCreateCertificates = (rows) => api.post("/certificates/bulk", { rows }).then((r) => r.data);

export const getE911 = () => api.get("/e911").then((r) => r.data);
export const createE911 = (payload) => api.post("/e911", payload).then((r) => r.data);
export const updateE911 = (id, payload) => api.put(`/e911/${id}`, payload).then((r) => r.data);
export const deleteE911 = (id) => api.delete(`/e911/${id}`).then((r) => r.data);
export const bulkCreateE911 = (rows) => api.post("/e911/bulk", { rows }).then((r) => r.data);

export const getRooms = () => api.get("/rooms").then((r) => r.data);
export const createRoom = (payload) => api.post("/rooms", payload).then((r) => r.data);
export const updateRoom = (id, payload) => api.put(`/rooms/${id}`, payload).then((r) => r.data);
export const deleteRoom = (id) => api.delete(`/rooms/${id}`).then((r) => r.data);

export const getDrRunbook = () => api.get("/dr/runbook").then((r) => r.data);
export const getDrTests = () => api.get("/dr/tests").then((r) => r.data);
export const createDrTest = (payload) => api.post("/dr/tests", payload).then((r) => r.data);
export const updateDrTest = (id, payload) => api.put(`/dr/tests/${id}`, payload).then((r) => r.data);
export const deleteDrTest = (id) => api.delete(`/dr/tests/${id}`).then((r) => r.data);

export const getFirmwareInsights = () => api.get("/firmware/insights").then((r) => r.data);

export const getReadiness = (siteId) => api.get(`/readiness/${siteId}`).then((r) => r.data);
export const setReadiness = (siteId, payload) => api.put(`/readiness/${siteId}`, payload).then((r) => r.data);

export const getChanges = () => api.get("/changes").then((r) => r.data);
export const createChange = (payload) => api.post("/changes", payload).then((r) => r.data);
export const updateChange = (id, payload) => api.put(`/changes/${id}`, payload).then((r) => r.data);
export const deleteChange = (id) => api.delete(`/changes/${id}`).then((r) => r.data);

export const getRunbook = (track = "webex") => api.get("/runbook", { params: { track } }).then((r) => r.data);
export const getRunbookState = (siteId, track = "webex") => api.get("/runbook/state", { params: { site_id: siteId, track } }).then((r) => r.data);
export const updateRunbookTask = (taskId, siteId, payload, track = "webex") => api.put(`/runbook/task/${taskId}`, payload, { params: { site_id: siteId, track } }).then((r) => r.data);
export const updateRunbookGate = (phaseId, siteId, payload, track = "webex") => api.put(`/runbook/gate/${phaseId}`, payload, { params: { site_id: siteId, track } }).then((r) => r.data);
export const updateRunbookIntake = (siteId, answers, track = "webex") => api.put("/runbook/intake", { answers }, { params: { site_id: siteId, track } }).then((r) => r.data);
export const runbookPrefill = (siteId, track = "webex") => api.post("/runbook/prefill", null, { params: { site_id: siteId, track } }).then((r) => r.data);
export const getRunbookScoreboard = (track = "webex") => api.get("/runbook/scoreboard", { params: { track } }).then((r) => r.data);
export const getReadinessReport = (track = "webex") => api.get("/runbook/readiness-report", { params: { track } }).then((r) => r.data);
export const getRunbookTrends = () => api.get("/runbook/trends").then((r) => r.data);
export const getWavesCutover = () => api.get("/waves/cutover").then((r) => r.data);
export const updateWaveItem = (wave, payload) => api.put(`/waves/cutover/${encodeURIComponent(wave)}/item`, payload).then((r) => r.data);
export const wavesSignoff = (wave, payload) => api.post(`/waves/cutover/${encodeURIComponent(wave)}/signoff`, payload).then((r) => r.data);
export const wavesSuggest = () => api.post("/waves/suggest").then((r) => r.data);
export const wavesApply = (assignments) => api.post("/waves/apply", { assignments }).then((r) => r.data);
export const getPhoneModels = () => api.get("/firmware/models").then((r) => r.data);
export const lookupFirmware = (model, version) =>
  api.post("/firmware/lookup", { model, version }).then((r) => r.data);
export const aiFirmwareInsight = (model, version) =>
  api.post("/firmware/ai-insight", { model, version }).then((r) => r.data);
export const sendChat = (session_id, message, context) =>
  api.post("/assistant/chat", { session_id, message, context }).then((r) => r.data);
export const getChatHistory = (session_id) =>
  api.get(`/assistant/history/${session_id}`).then((r) => r.data);

// Session Reports
export const createReport = (payload) => api.post("/reports", payload).then((r) => r.data);
export const listReports = () => api.get("/reports").then((r) => r.data);
export const getReport = (shareId) => api.get(`/reports/${shareId}`).then((r) => r.data);
export const deleteReport = (id) => api.delete(`/reports/${id}`).then((r) => r.data);

export const getGuideHistory = (siteId) =>
  api.get("/runbook/guide/history", { params: { site_id: siteId || "estate" } }).then((r) => r.data);

// Streaming chat via fetch + ReadableStream (SSE). onDelta(text), onDone(), onError(msg)
export async function streamChat({ session_id, message, context }, { onDelta, onDone, onError }) {
  try {
    const resp = await fetch(`${API}/assistant/chat/stream`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id, message, context }),
    });
    if (!resp.ok || !resp.body) {
      const detail = await resp.json().catch(() => ({}));
      onError?.(detail?.detail || `HTTP ${resp.status}`);
      return;
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith("data:")) continue;
        const data = trimmed.slice(5).trim();
        if (data === "[DONE]") { onDone?.(); return; }
        try {
          const parsed = JSON.parse(data);
          if (parsed.delta) onDelta?.(parsed.delta);
          else if (parsed.error) onError?.(parsed.error);
        } catch (_) { /* ignore partial */ }
      }
    }
    onDone?.();
  } catch (e) {
    onError?.(e.message || "stream failed");
  }
}

// Streaming migration guide (SSE). payload: { message, site_id, phase_name, task_title }
export async function streamGuide(payload, { onDelta, onDone, onError }) {
  try {
    const resp = await fetch(`${API}/runbook/guide/stream`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!resp.ok || !resp.body) {
      const detail = await resp.json().catch(() => ({}));
      onError?.(detail?.detail || `HTTP ${resp.status}`);
      return;
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith("data:")) continue;
        const data = trimmed.slice(5).trim();
        if (data === "[DONE]") { onDone?.(); return; }
        try {
          const parsed = JSON.parse(data);
          if (parsed.delta) onDelta?.(parsed.delta);
          else if (parsed.error) onError?.(parsed.error);
        } catch (_) { /* ignore partial */ }
      }
    }
    onDone?.();
  } catch (e) {
    onError?.(e.message || "stream failed");
  }
}
